import { getCookieValue } from '@config/axeptio';
import { getEnvVar } from '@config/configEnv';
import { TRACKING_COOKIE_AMPLITUDE, TRACKING_COOKIE_NAME } from '@utils/consts/cookie';
import { EventProps } from '@utils/types/amplitude/events';
import { UserProperties } from '@utils/types/amplitude/userProperties';

let allowAmplitudeTracking = false;
let amplitudeInitPromise: Promise<void> | null = null;
let isSessionReplayPluginAdded = false;

// Amplitude relies on the window object and should only be run in the browser (not server side)
const isBrowser = typeof window !== 'undefined';
const AMPLITUDE_API_KEY = getEnvVar('AMPLITUDE_API_KEY');

const initializeAmplitude = async (): Promise<void> => {
    if (amplitudeInitPromise) {
        await amplitudeInitPromise;

        return;
    }

    amplitudeInitPromise = (async () => {
        const amplitude = await import('@amplitude/analytics-browser');

        amplitude.init(AMPLITUDE_API_KEY, {
            autocapture: {
                attribution: false,
                elementInteractions: true, // Visual Labeling
                fileDownloads: false,
                formInteractions: true,
                pageViews: true,
                sessions: true, // Session Replay
            },
            serverUrl: '/api/client-events',
        });

        if (!isSessionReplayPluginAdded) {
            const { sessionReplayPlugin } = await import(
                '@amplitude/plugin-session-replay-browser'
            );
            // sampleRate of 1 captures 100% of all sessions - not advisable for production environment
            // This parameter is set from the remote configuration available in the Amplitude dashboard
            amplitude.add(sessionReplayPlugin());
            isSessionReplayPluginAdded = true;
        }
    })().catch((error) => {
        amplitudeInitPromise = null;
        throw error;
    });

    await amplitudeInitPromise;
};

export const launchAmplitude = async (isLoggedUser: boolean): Promise<void> => {
    // Automatic allow tracking on sign up, after CGU acceptation
    allowAmplitudeTracking = isLoggedUser
        ? true
        : isBrowser && getCookieValue(TRACKING_COOKIE_NAME).includes(TRACKING_COOKIE_AMPLITUDE);
    if (allowAmplitudeTracking) {
        await initializeAmplitude();
    }
};

export const track = async ({ name, params }: EventProps): Promise<void> => {
    // we check the initialization of amplitude as the logging can happen too early for the pageview
    if (allowAmplitudeTracking) {
        await initializeAmplitude();
        const amplitude = await import('@amplitude/analytics-browser');
        amplitude.track(name, params);
    }
};

export const setUserId = async (userId: string | undefined): Promise<void> => {
    if (allowAmplitudeTracking) {
        await initializeAmplitude();
        const amplitude = await import('@amplitude/analytics-browser');
        amplitude.setUserId(userId);
    }
};

export const setUserProperties = async (
    userId: string | undefined,
    props: UserProperties,
): Promise<void> => {
    if (allowAmplitudeTracking) {
        await initializeAmplitude();
        const amplitude = await import('@amplitude/analytics-browser');
        await setUserId(userId);
        const identify = new amplitude.Identify();
        Object.entries(props).forEach(([key, value]) => identify.set(key, value));
        amplitude.identify(identify);
    }
};
